import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/brand/brand/apps/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Label } from '@primer/react';
import { PropTableValues } from '../../src/components';
import { Link, IDEFileExtensions } from '@primer/react-brand';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {IDE} from '@primer/react-brand'
`}</code></pre>
    <h2>{`Examples`}</h2>
    <h3>{`Default`}</h3>
    <Note variant="warning" mdxType="Note">
      <p>{`The `}<inlineCode parentName="p">{`IDE`}</inlineCode>{` does not automatically apply syntax highlighting to code snippets. Usage of this component in GitHub.com is expected to use the proprietary tokenizer and highlighter, the output of which can be passed directly into `}<inlineCode parentName="p">{`IDE`}</inlineCode>{` as a parameter.`}</p>
    </Note>
    <Note variant="info" mdxType="Note">
      <p>{`See Storybook for examples of `}<inlineCode parentName="p">{`IDE`}</inlineCode>{` using `}<inlineCode parentName="p">{`highlight.js`}</inlineCode>{` syntax highlighter.`}</p>
      <Link href="https://primer.style/brand/storybook/?path=/story/components-ide--default" mdxType="Link">
  See syntax highlighter examples
      </Link>
    </Note>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<IDE height={500}>
  <IDE.Editor
    showReplayButton={false}
    files={[
      {
        name: 'index.js',
        alternativeText:
          'TypeScript sentiment analysis function with D3.js visualization.',
        code: \`import { fetch } from "fetch-h2";

async function isPositive(text: string): Promise<boolean> {
  const response = await fetch(...)
  const drawScatterplot = (data, height, width) => {
    const svg = d3.select("#scatterplot")
      .data(data)
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", 5)
  }
  const json = await response.json();
  return json.label === "pos";
}\`,
      },
      {
        name: 'parse_expenses.py',
        alternativeText:
          'Python function that converts financial transaction strings into structured data, parsing dates, amounts, and currencies while skipping comments',
        code: \`import datetime

def parse_expenses(expenses_string):
Ignore lines starting with #.
Parse the date using datetime.
Example expenses_string:
2016-01-02 -34.01 USD
2016-01-03 2.59 DKK
2016-01-03 -2.72 EUR
"""
expenses = []
for line in expenses_string.splitlines():
if line.startswith("#"):
continue
date, value, currency = line.split(" ")
expenses.append((datetime.datetime.strptime(date, "%Y-%m-%d"),
          float(value),
          currency))
return expenses\`,
      },
    ]}
  />
</IDE>
`}</code></pre>
    <h3>{`Animations`}</h3>
    <p>{`Line-by-line animations can be enabled using pre-formatted prop values. A replay button is enabled by default.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<IDE height={500}>
  <IDE.Editor
    files={[
      {
        name: 'index.js',
        alternativeText:
          'TypeScript sentiment analysis function with D3.js visualization.',

        code: \`import { fetch } from "fetch-h2";

async function isPositive(text: string): Promise<boolean> {
  const response = await fetch(...)
  const drawScatterplot = (data, height, width) => {
    const svg = d3.select("#scatterplot")
      .data(data)
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", 5)
  }
  const json = await response.json();
  return json.label === "pos";
}\`
          .split('\\n')
          .map((line) => line || ' '),
      },
    ]}
  />
</IDE>
`}</code></pre>
    <h3>{`GitHub Copilot suggestions`}</h3>
    <p>{`Simulate GitHub Copilot suggestions using the `}<inlineCode parentName="p">{`suggestedLineStart`}</inlineCode>{` property.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<IDE height={500}>
  <IDE.Editor
    files={[
      {
        name: 'index.js',
        // Line number at which the suggestion will apply
        suggestedLineStart: 6,
        alternativeText:
          'TypeScript sentiment analysis function with D3.js visualization. Copilot suggests completing the scatterplot with SVG creation and circle attributes.',

        code: \`import { fetch } from "fetch-h2";

async function isPositive(text: string): Promise<boolean> {
  const response = await fetch(...)
  const drawScatterplot = (data, height, width) => {
    const svg = d3.select("#scatterplot")
      .data(data)
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", 5)
  }
  const json = await response.json();
  return json.label === "pos";
}\`
          .split('\\n')
          .map((line) => line || ' '),
      },
    ]}
  />
</IDE>
`}</code></pre>
    <h3>{`GitHub Copilot chat`}</h3>
    <p>{`Simulate GitHub Copilot Chat using the `}<inlineCode parentName="p">{`IDE.Chat`}</inlineCode>{` sub-component. The script is fully customizable, and code snippets will require pre-formatting to enable syntax highlighting.`}</p>
    <Note variant="info" mdxType="Note">
      <p>{`See Storybook for a comprehensive example of the Chat feature, inclusive of syntax highlighting.`}</p>
      <Link href="https://primer.style/brand/storybook/?path=/story/components-ide-features--chat-only" mdxType="Link">
  See Chat example in Storybook
      </Link>
    </Note>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<IDE height={700}>
  <IDE.Chat
    alternativeText="A user asks how to concatenate arrays in JavaScript, Copilot demonstrates using the concat method, and the user confirms it worked."
    script={[
      {
        role: 'user',
        handle: 'monalisa',
        avatar: '/brand/assets/avatar-mona.png',
        message: 'How do I concatenate two arrays in JavaScript?',
      },
      {
        role: 'assistant',
        handle: 'GitHub Copilot',
        message:
          "To concatenate two arrays in JavaScript, you can use the \`concat\` method. Here's an example:",
        codeSnippet: \`const array1 = [1, 2, 3];\\nconst array2 = [4, 5, 6];\\nconst resultArray = array1.concat(array2);\\nconsole.log(resultArray); // Output: [1, 2, 3, 4, 5, 6]\`,
      },
      {
        role: 'user',
        handle: 'monalisa',
        avatar: '/brand/assets/avatar-mona.png',
        message: 'It worked!',
      },
    ]}
  />
</IDE>
`}</code></pre>
    <h3>{`Glass variant`}</h3>
    <p>{`A `}<inlineCode parentName="p">{`glass`}</inlineCode>{` variant is available to simplify placement of the `}<inlineCode parentName="p">{`IDE`}</inlineCode>{` on background images. This variant adds alpha transparency and background blurring.`}</p>
    <p>{`When using the `}<inlineCode parentName="p">{`glass`}</inlineCode>{` variant, pay close attention to the background image behind the tabs of the `}<inlineCode parentName="p">{`IDE.Editor`}</inlineCode>{`. Ensure there is sufficient contrast between the text and the background on inactive tabs.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live",
        "live": true
      }}>{`<Box
  padding={48}
  borderRadius="large"
  style={{
    backgroundImage: \`url(https://github.com/primer/brand/assets/13340707/7fe496dc-f6e0-417e-9453-32cec638ca68)\`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  }}
>
  <IDE height={400} variant="glass">
    <IDE.Editor
      showReplayButton={false}
      files={[
        {
          name: 'index.js',
          alternativeText:
            'TypeScript sentiment analysis function with D3.js visualization.',
          code: \`import { fetch } from "fetch-h2";

async function isPositive(text: string): Promise<boolean> {
  const response = await fetch(...)
  const drawScatterplot = (data, height, width) => {
    const svg = d3.select("#scatterplot")
      .data(data)
      .attr("cx", d => d.x)
      .attr("cy", d => d.y)
      .attr("r", 5)
  }
  const json = await response.json();
  return json.label === "pos";
}\`,
        },
        {
          name: 'parse_expenses.py',
          alternativeText:
            'Python function description and docstring showing how to parse financial transactions with example date, amount, and currency formats.',

          code: \`import datetime

def parse_expenses(expenses_string):
Ignore lines starting with #.
Parse the date using datetime.
Example expenses_string:
2016-01-02 -34.01 USD
2016-01-03 2.59 DKK
2016-01-03 -2.72 EUR
"""\`,
        },
      ]}
    />
  </IDE>
</Box>
`}</code></pre>
    <h2>{`Accessibility`}</h2>
    <p>{`The full contents of `}<inlineCode parentName="p">{`IDE.Chat`}</inlineCode>{` and `}<inlineCode parentName="p">{`IDE.Editor`}</inlineCode>{` are intentionally not made available to users of assistive technologies. Summaries of the content — which must be provided using the `}<inlineCode parentName="p">{`alternativeText`}</inlineCode>{` prop — are made available instead.`}</p>
    <p>{`When writing `}<inlineCode parentName="p">{`alternativeText`}</inlineCode>{`, consider the following:`}</p>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`IDE.Chat`}</inlineCode>{` script should be summarized in a way that conveys the essential information of the conversation.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`IDE.Editor`}</inlineCode>{` code should be summarized in a way that conveys the essential information of the code snippet, specifically noting any suggestions made by Copilot.`}</li>
    </ul>
    <p>{`In both cases, the goal is to provide a meaningful summary of the content that will allow users of assistive technologies to understand the purpose and scope of the conversation or code snippet, without needing to understand the full content.`}</p>
    <h2>{`Component props`}</h2>
    <h3>{`IDE `}<Label mdxType="Label">{`Required`}</Label></h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`default`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`required`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`height`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`800`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The optionally configurable height of the IDE`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`variant`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><PropTableValues values={['default', 'glass']} addLineBreaks mdxType="PropTableValues" /></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`default`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Alternative presentation of the IDE`}</td>
        </tr>
      </tbody>
    </table>
    <h3>{`IDE.Editor`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`default`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`required`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`activeTab`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Active tab index`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`files`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#"
            }}><inlineCode parentName="a">{`IDEEditorFile[]`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Array of editor files`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`showLineNumbers`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Show line numbers in the editor`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`size`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><PropTableValues values={['small', 'medium', 'large']} addLineBreaks mdxType="PropTableValues" /></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Controls editor text size`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`triggerAnimation`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Trigger animation externally`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`showReplayButton`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Show replay button`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Supported file extensions in the editor include: `}<PropTableValues values={IDEFileExtensions} removeApostrophes commaSeparated mdxType="PropTableValues" /></p>
    <h3>{`IDE.Chat`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`default`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`required`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`script`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#"
            }}><inlineCode parentName="a">{`IDEChatMessage[]`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Array of chat messages`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`alternativeText`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Alternative description of the chat script for users of assistive technologies`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`animationDelay`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`3000`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Delay for the chat animation`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      